import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { readableError } from "@/lib/errors";

// Same-origin `/api` — Emergent's ingress routes `/api/*` to the backend on
// every frontend host (preview, .emergent.host, custom domain), so no
// `REACT_APP_BACKEND_URL` swap is needed and CORS never applies.
const API = "/api";

/**
 * Lead-capture dialog for the gated /pricing page. Collects the minimum
 * viable info (name / email / phone) then flips the parent `revealed` flag
 * so tier cards show their real prices and hand off to the register CTA.
 */
export default function RevealDialog({ open, onOpenChange, tier, onRevealed }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/pricing-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, tier: tier?.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(readableError(body, "Something went wrong."));
      }
      // Persist across page reloads for the same visitor.
      try {
        localStorage.setItem("bookaway_pricing_revealed", "1");
      } catch {}
      toast.success("Pricing unlocked.");
      onRevealed?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Could not save your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pricing-reveal-dialog"
        className="max-w-md border-black/10 bg-[#F7F5F2] text-[#2A2724] p-0 overflow-hidden"
      >
        <div className="h-[3px] w-full bg-gradient-to-r from-[#C9A85F] via-[#E6D3A3] to-[#C9A85F]" />
        <form onSubmit={submit} className="px-8 pb-8 pt-7">
          <p className="overline text-gold mb-3">Membership pricing</p>
          <DialogTitle className="font-serif text-3xl font-semibold leading-tight text-[#2A2724]">
            See our plans.
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed text-[#6B6B6B]">
            A few quick details and we'll unlock the price list. No spam —
            just a courtesy follow-up from a real person on our team.
          </DialogDescription>

          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">
                Your name
              </label>
              <input
                data-testid="reveal-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Alex Rivera"
                className="w-full rounded-xl border border-black/15 bg-white/60 px-4 py-3 text-sm text-[#2A2724] placeholder:text-[#9a9a9a] outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">
                Work email
              </label>
              <input
                data-testid="reveal-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@studio.com"
                className="w-full rounded-xl border border-black/15 bg-white/60 px-4 py-3 text-sm text-[#2A2724] placeholder:text-[#9a9a9a] outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">
                Phone
              </label>
              <input
                data-testid="reveal-phone"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+61 400 000 000"
                className="w-full rounded-xl border border-black/15 bg-white/60 px-4 py-3 text-sm text-[#2A2724] placeholder:text-[#9a9a9a] outline-none focus:border-gold"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="reveal-submit"
            disabled={submitting}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627] disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Unlocking…
              </>
            ) : (
              "Reveal pricing"
            )}
          </button>

          <p className="mt-4 text-[0.7rem] uppercase tracking-[0.2em] text-[#6B6B6B]/80">
            By continuing you agree to be contacted about Book Away.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
